import type { ReactText } from 'react';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import type { ModalProps } from '../ui/Modal/Modal';
import { Modal } from '../ui';
import type { CoaFormSaveType } from './CoaForm';
import CoaForm from './CoaForm';
import type { NotNullDataPrice } from './coa';
import { useCreateCoaWithRelationsMutation } from './coa';
import { useEditCoaModalQuery } from '../../graphql';
import { LoadingOverlay } from '../LoadingSpinner';
import { useCurrentUser } from '../../state/zustand';
import { isDefined } from '../../lib/util';
import { ModalContext } from '../contexts/ModalContext';
import { handleCoaError } from './coaErrorHandling';

export interface NewCoaModalProps extends Omit<ModalProps, 'isOpen'> {
  onCreate?: () => void;
  coaIdToDuplicate?: number;
}

function omitId<T extends { id: number | null }>({
  id,
  ...objWithoutId
}: T): Omit<T, 'id'> {
  return objWithoutId;
}

const NewCoaModal = ({
  coaIdToDuplicate,
  onCreate,
  onClose,
  ...restProps
}: NewCoaModalProps): JSX.Element => {
  const [createCoaWithRelations, { loading: isLoading }] =
    useCreateCoaWithRelationsMutation();
  const currentUser = useCurrentUser();
  const saveToastId = useRef<ReactText | null>(null);
  const openModal = useContext(ModalContext).openModal;
  const orgType: 'shipowner' | 'charterer' | null = useMemo(
    () =>
      currentUser?.organization?.shipOwner
        ? 'shipowner'
        : currentUser?.organization?.charterer
          ? 'charterer'
          : null,
    [currentUser],
  );

  const defaultCoa = {
    ...(orgType === 'shipowner'
      ? {
          shipownerOrgId: currentUser?.organizationId ?? -1,
          organizationByShipownerOrgId: currentUser?.organization,
        }
      : {}),
    ...(orgType === 'charterer'
      ? {
          chartererOrgId: currentUser?.organizationId ?? -1,
          organizationByChartererOrgId: currentUser?.organization,
        }
      : {}),
  };

  const {
    data: coaToDuplicateData,
    loading: coaToDuplicateLoading,
    error: coaToDuplicateError,
  } = useEditCoaModalQuery({
    variables: { coaId: coaIdToDuplicate ?? NaN },
    skip: coaIdToDuplicate === undefined,
    fetchPolicy: 'network-only',
  });

  const handleSave = async (data: CoaFormSaveType): Promise<unknown> => {
    const userId = currentUser?.id ?? null;
    if (!userId) return;

    saveToastId.current = toast('Saving COA', {
      type: toast.TYPE.INFO,
      isLoading: true,
    });

    const notNullDataPrices = data.prices
      .map(omitId)
      .filter(({ price }) => isDefined(price)) as NotNullDataPrice[];

    try {
      const coaId = await createCoaWithRelations(
        {
          ...data.baseContract,
          userId,
          chartererId: userId,
          externalId: data.baseContract.externalId?.length
            ? data.baseContract.externalId
            : null,
        },
        data.bunkers.map(omitId),
        data.portPairs.map((portPair) => ({
          chartererId: userId,
          ...portPair,
        })),
        data.portData.map(omitId),
        data.assortments,
        notNullDataPrices,
        data.vessels.map(omitId),
        data.commissions,
        data.portCosts,
      );

      toast.update(saveToastId.current, {
        render: 'COA saved successfully',
        type: toast.TYPE.SUCCESS,
        isLoading: false,
      });
      onCreate?.();
      onClose?.();
      if (!data.closable) {
        openModal({
          type: 'EDIT_COA',
          props: { coaId },
        });
      }
    } catch (error) {
      handleCoaError(
        error,
        saveToastId.current,
        data.baseContract.externalId ?? undefined,
      );
    }
  };

  useEffect(() => {
    if (coaToDuplicateError) {
      toast.error(
        `Error loading COA to duplicate ${coaToDuplicateError.message}`,
      );
      onClose?.();
    }
  }, [coaToDuplicateError, onClose]);

  return (
    <Modal isOpen onClose={onClose} {...restProps}>
      {coaToDuplicateLoading && (
        <LoadingOverlay message="Loading COA to duplicate" />
      )}
      {!coaToDuplicateLoading && !coaToDuplicateError && (
        <CoaForm
          onSave={handleSave}
          onClose={onClose}
          coa={coaToDuplicateData?.coaContractById ?? defaultCoa}
          duplicate={true}
          isLoading={isLoading}
        />
      )}
    </Modal>
  );
};

export default NewCoaModal;
